import * as React from 'react';

import { prefersReducedMotion, useTransitionStageDriver } from './transitionStageDriver';

/**
 * Lifecycle of a presence-transitioned element.
 *
 * The two `pre*` stages exist so the element can commit its transition-start
 * styles (and measure the DOM) one frame before the target styles land —
 * without them the browser coalesces both style writes and no transition runs.
 */
export type PresenceStage = 'preEnter' | 'entering' | 'open' | 'preExit' | 'exiting';

/** {@link useTransitionedPresence}'s internal state, exported for the pure reducers below. */
export interface IPresenceState<T> {
  /** What to render — retains the last non-null value through the exit stages. */
  renderValue: T | null;
  /** Meaningful only while `renderValue` is non-null. */
  stage: PresenceStage;
}

/** A value appeared (or changed). An interrupted exit re-enters from wherever the transition left it. */
export const presenceOnShow = <T>(state: IPresenceState<T>, value: T): IPresenceState<T> => {
  if (state.renderValue === null) {
    return { renderValue: value, stage: 'preEnter' };
  }

  if (state.stage === 'preExit' || state.stage === 'exiting') {
    return { renderValue: value, stage: 'entering' };
  }

  return { renderValue: value, stage: state.stage };
};

/** The value went null. An interrupted enter exits from wherever the transition left it. */
export const presenceOnHide = <T>(state: IPresenceState<T>): IPresenceState<T> => {
  if (state.renderValue === null || state.stage === 'preExit' || state.stage === 'exiting') {
    return state;
  }

  if (state.stage === 'open') {
    return { ...state, stage: 'preExit' };
  }

  return { ...state, stage: 'exiting' };
};

/**
 * A running transition finished (or the fallback timer fired): an enter lands
 * on `open`, an exit unmounts. Also settles a `pre*` stage whose rAF never ran
 * (throttled background tab) so the element cannot get stuck invisible.
 */
export const presenceOnSettle = <T>(state: IPresenceState<T>): IPresenceState<T> => {
  if (state.renderValue === null || state.stage === 'open') {
    return state;
  }

  if (state.stage === 'preExit' || state.stage === 'exiting') {
    return { renderValue: null, stage: 'open' };
  }

  return { ...state, stage: 'open' };
};

/** The pre → active flips the double-rAF drives. Anything else is left for {@link presenceOnSettle}. */
export const presenceOnFrame = <T>(state: IPresenceState<T>): IPresenceState<T> => {
  if (state.stage === 'preEnter') {
    return { ...state, stage: 'entering' };
  }

  if (state.stage === 'preExit') {
    return { ...state, stage: 'exiting' };
  }

  return state;
};

interface ITransitionedPresenceOptions {
  /** The CSS property whose `transitionend` settles a stage — see `useTransitionStageDriver`. */
  transitionProperty: string;
  /** The CSS transition's duration. The fallback timer fires this plus a margin after each stage starts. */
  durationMs: number;
}

export interface ITransitionedPresence<T> {
  /** The content to render — holds the last non-null value through the exit. Null once fully closed. */
  renderValue: T | null;
  /** Which styles the element should wear this frame. */
  stage: PresenceStage;
  /** Attach to the transitioning element itself; settles the enter and unmounts after the exit. */
  onTransitionEnd: React.TransitionEventHandler;
}

/**
 * Keeps an element mounted through a CSS enter/exit transition and tells it
 * which stage of that transition it is in.
 *
 * The caller maps stages to styles: transition-start styles on `preEnter` /
 * `exiting`, target styles on `entering` / `open` / `preExit`. A first render
 * with a non-null value starts at `open` with no animation (nothing should
 * slide on page load), reversals mid-transition animate back from wherever the
 * transition currently is without unmounting, and a `prefers-reduced-motion`
 * viewer gets instant mounts and unmounts with no staging at all.
 */
export const useTransitionedPresence = <T>(
  value: T | null,
  { transitionProperty, durationMs }: ITransitionedPresenceOptions
): ITransitionedPresence<T> => {
  const [state, setState] = React.useState<IPresenceState<T>>({ renderValue: value, stage: 'open' });

  const prevValueRef = React.useRef(value);
  React.useEffect(() => {
    if (value === prevValueRef.current) {
      return;
    }
    prevValueRef.current = value;

    if (prefersReducedMotion()) {
      setState({ renderValue: value, stage: 'open' });
      return;
    }

    setState((s) => (value === null ? presenceOnHide(s) : presenceOnShow(s, value)));
  }, [value]);

  const { renderValue, stage } = state;

  const onTransitionEnd = useTransitionStageDriver({
    isTransitioning: renderValue !== null && stage !== 'open',
    stageKey: stage,
    durationMs,
    transitionProperty,
    onFrame: () => setState(presenceOnFrame),
    onSettle: () => setState(presenceOnSettle),
  });

  return { renderValue, stage, onTransitionEnd };
};
